import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  ImagePlus,
  Trash2,
  Power,
  PowerOff,
  ArrowUp,
  ArrowDown,
  Link as LinkIcon,
  Pencil,
  X,
} from "lucide-react";

import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";
import { db, storage } from "../web/src/firebaseClient";

const COLLECTION_NAME = "banners";

export default function AdminBannerManagement() {
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(null);
  const [banners, setBanners] = useState([]);
  const [editingBanner, setEditingBanner] = useState(null);

  const [title, setTitle] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [ctaText, setCtaText] = useState("Detayları Gör");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkAdmin();
    fetchBanners();
  }, []);

  const checkAdmin = async () => {
    const user = getAuth().currentUser;

    if (!user) {
      setIsAdmin(false);
      return;
    }

    const token = await user.getIdTokenResult();
    setIsAdmin(token.claims.role === "admin");
  };

  const fetchBanners = async () => {
    const q = query(collection(db, COLLECTION_NAME), orderBy("order", "asc"));
    const snapshot = await getDocs(q);

    const data = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setBanners(data);
  };

  const toDateInputValue = (dateValue) => {
    if (!dateValue) return "";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

    return localDate.toISOString().slice(0, 16);
  };

  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    setTitle(banner.title || "");
    setTargetUrl(banner.targetUrl || "");
    setCtaText(banner.ctaText || "Detayları Gör");
    setStartDate(toDateInputValue(banner.startDate));
    setEndDate(toDateInputValue(banner.endDate));
    setIsActive(Boolean(banner.isActive));
    setImageFile(null);
    setPreviewUrl(banner.imageUrl || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmitBanner = async (e) => {
    e.preventDefault();

    if (!editingBanner && !imageFile) {
      alert("Lütfen 16:9 formatında bir görsel seçin.");
      return;
    }

    if (!targetUrl.trim()) {
      alert("Lütfen bir URL ekleyin.");
      return;
    }

    setLoading(true);

    try {
      let imageUrl = editingBanner?.imageUrl || "";
      let storagePath = editingBanner?.storagePath || "";

      if (imageFile) {
        const fileName = `banners/${Date.now()}-${imageFile.name}`;
        const imageRef = ref(storage, fileName);

        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
        storagePath = fileName;

        if (editingBanner?.storagePath) {
          try {
            await deleteObject(ref(storage, editingBanner.storagePath));
          } catch (error) {
            console.warn("Eski görsel silinemedi:", error);
          }
        }
      }

      const payload = {
        title: title.trim(),
        imageUrl,
        storagePath,
        targetUrl: targetUrl.trim(),
        ctaText: ctaText.trim() || "Detayları Gör",
        isActive,
        startDate: startDate ? Timestamp.fromDate(new Date(startDate)) : null,
        endDate: endDate ? Timestamp.fromDate(new Date(endDate)) : null,
        updatedAt: serverTimestamp(),
      };

      if (editingBanner) {
        await updateDoc(doc(db, COLLECTION_NAME, editingBanner.id), payload);
      } else {
        await addDoc(collection(db, COLLECTION_NAME), {
          ...payload,
          order: banners.length + 1,
          clickCount: 0,
          createdAt: serverTimestamp(),
        });
      }

      resetForm();
      await fetchBanners();
    } catch (error) {
      console.error(error);
      alert("Manşet kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingBanner(null);
    setTitle("");
    setTargetUrl("");
    setCtaText("Detayları Gör");
    setStartDate("");
    setEndDate("");
    setIsActive(true);
    setImageFile(null);
    setPreviewUrl("");
  };

  const toggleActive = async (banner) => {
    await updateDoc(doc(db, COLLECTION_NAME, banner.id), {
      isActive: !banner.isActive,
      updatedAt: serverTimestamp(),
    });

    await fetchBanners();
  };

  const deleteBanner = async (banner) => {
    const confirmDelete = window.confirm("Bu manşeti silmek istiyor musunuz?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, COLLECTION_NAME, banner.id));

      if (banner.storagePath) {
        try {
          await deleteObject(ref(storage, banner.storagePath));
        } catch (error) {
          console.warn("Görsel silinemedi:", error);
        }
      }

      if (editingBanner?.id === banner.id) {
        resetForm();
      }

      await fetchBanners();
    } catch (error) {
      console.error(error);
      alert("Manşet silinirken bir hata oluştu.");
    }
  };

  const moveBanner = async (index, direction) => {
    const newIndex = direction === "up" ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= banners.length) return;

    const currentBanner = banners[index];
    const targetBanner = banners[newIndex];

    await updateDoc(doc(db, COLLECTION_NAME, currentBanner.id), {
      order: targetBanner.order,
      updatedAt: serverTimestamp(),
    });

    await updateDoc(doc(db, COLLECTION_NAME, targetBanner.id), {
      order: currentBanner.order,
      updatedAt: serverTimestamp(),
    });

    await fetchBanners();
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "Belirtilmedi";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

    return date.toLocaleString("tr-TR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  if (isAdmin === null) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Bu sayfaya erişim yetkiniz yok
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef5ff]">
      <InnovationHubHeader />

      <section className="p-6">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mb-4 text-sm font-medium text-[#1565C0] hover:underline"
            >
              ← Admin Panel
            </button>

            <h1 className="text-2xl font-semibold text-[#173b7a]">
              Manşet Yönetimi
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Ana sayfada gösterilecek 16:9 formatındaki manşet görsellerini
              buradan yönetebilirsiniz.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-[#173b7a]">
                {editingBanner ? "Manşet Güncelle" : "Yeni Manşet Ekle"}
              </h2>

              {editingBanner && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-200"
                >
                  <X size={16} />
                  Vazgeç
                </button>
              )}
            </div>

            <form onSubmit={handleSubmitBanner} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Başlık
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Örn: Yeni dönem başvuruları başladı"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Görsel — 16:9 önerilir
                </label>

                <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-100 bg-[#f8fbff] p-6 text-center transition hover:border-[#1565C0]/40">
                  <ImagePlus className="mb-2 text-[#1565C0]" size={32} />
                  <span className="text-sm font-medium text-[#173b7a]">
                    {editingBanner ? "Yeni görsel seç" : "Görsel seç"}
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    PNG, JPG veya WEBP
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>

                {previewUrl && (
                  <div className="mt-4 overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
                    <img
                      src={previewUrl}
                      alt="Manşet önizleme"
                      className="aspect-video w-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Link URL
                  </label>
                  <input
                    type="text"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="/itk-starter veya https://..."
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Buton Metni
                  </label>
                  <input
                    type="text"
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="Detayları Gör"
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Bitiş Tarihi
                  </label>
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-[#f8fbff] p-4">
                <div>
                  <p className="text-sm font-medium text-[#173b7a]">
                    Yayın Durumu
                  </p>
                  <p className="text-xs text-gray-500">
                    Aktif seçilirse tarih aralığına göre yayında görünür.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsActive((prev) => !prev)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {isActive ? "Aktif" : "Pasif"}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0d4f9c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading
                  ? "Kaydediliyor..."
                  : editingBanner
                  ? "Manşeti Güncelle"
                  : "Manşeti Kaydet"}
              </button>
            </form>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-lg font-semibold text-[#173b7a]">
              Mevcut Manşetler
            </h2>

            {banners.length === 0 ? (
              <div className="rounded-xl bg-[#f8fbff] p-6 text-center text-sm text-gray-500">
                Henüz manşet eklenmemiş.
              </div>
            ) : (
              <div className="space-y-4">
                {banners.map((banner, index) => (
                  <div
                    key={banner.id}
                    className={`grid grid-cols-1 gap-4 rounded-2xl border p-4 md:grid-cols-[220px_1fr_auto] ${
                      editingBanner?.id === banner.id
                        ? "border-[#1565C0] bg-blue-50/60"
                        : "border-gray-100 bg-[#f8fbff]"
                    }`}
                  >
                    <img
                      src={banner.imageUrl}
                      alt={banner.title || "Manşet görseli"}
                      className="aspect-video w-full rounded-xl object-cover"
                    />

                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-[#173b7a]">
                          {banner.title || "Başlıksız Manşet"}
                        </h3>

                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            banner.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {banner.isActive ? "Aktif" : "Pasif"}
                        </span>
                      </div>

                  <div className="flex items-center gap-2 text-sm text-gray-500">
  <LinkIcon size={15} />

  {banner.targetUrl?.trim().startsWith("http://") ||
  banner.targetUrl?.trim().startsWith("https://") ? (
    <a
      href={banner.targetUrl.trim()}
      target="_blank"
      rel="noreferrer"
      className="break-all hover:text-[#1565C0] hover:underline"
    >
      {banner.targetUrl}
    </a>
  ) : (
    <span className="break-all text-[#1565C0]">
      {banner.targetUrl}
    </span>
  )}
</div>

                      <p className="text-sm text-gray-500">
                        Buton:{" "}
                        <span className="font-medium text-gray-700">
                          {banner.ctaText || "Detayları Gör"}
                        </span>
                      </p>

                      <p className="text-xs text-gray-500">
                        Başlangıç: {formatDate(banner.startDate)}
                      </p>

                      <p className="text-xs text-gray-500">
                        Bitiş: {formatDate(banner.endDate)}
                      </p>

                      <p className="text-xs text-gray-400">
                        Sıra: {banner.order}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 md:flex-col">
                      <button
                        type="button"
                        onClick={() => moveBanner(index, "up")}
                        disabled={index === 0}
                        className="rounded-lg bg-white p-2 text-gray-600 shadow-sm transition hover:text-[#1565C0] disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <ArrowUp size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => moveBanner(index, "down")}
                        disabled={index === banners.length - 1}
                        className="rounded-lg bg-white p-2 text-gray-600 shadow-sm transition hover:text-[#1565C0] disabled:cursor-not-allowed disabled:opacity-30"
                      >
                        <ArrowDown size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleEditBanner(banner)}
                        className="rounded-lg bg-white p-2 text-[#1565C0] shadow-sm transition hover:bg-blue-50"
                      >
                        <Pencil size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleActive(banner)}
                        className="rounded-lg bg-white p-2 shadow-sm transition hover:text-[#1565C0]"
                      >
                        {banner.isActive ? (
                          <PowerOff size={18} />
                        ) : (
                          <Power size={18} />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteBanner(banner)}
                        className="rounded-lg bg-white p-2 text-red-500 shadow-sm transition hover:bg-red-50"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <InnovationHubFooter/>
    </main>
  );
}